import React, { useState } from 'react';

import { IndexGridTree } from '@rockerbox/styleguide';
import toast from 'react-hot-toast';
import { Modal, Input, Button, Message } from 'semantic-ui-react';

import { isValidRoleName, arePermissionsEqual } from './helpers';
import { useFormData } from './hooks';
import { track } from '../../../../utils/tracking';
import { postRolesInRBAC, putRoleInRBAC, postPermissionsForRoleInRBAC, putPermissionsForRoleInRBAC } from '../../../api/authTools';
import { RBAC_SYSTEM_ROLES } from '../../../constants/rbac';
import { useGlobalState } from '../../../hooks/global';
import { useRBACPermissionsByRole, useRBACPermissions } from '../../../hooks/useRBAC';
import { formatName } from '../../../utils/valueFormatter';

const PermissionForm = ({ trigger, role, onSaveSuccess, disabled = false }) => {
  const { accessibleFeatures, account } = useGlobalState();
  const { allPermissions } = useRBACPermissions();
  const { permissions, refreshPermissions } = useRBACPermissionsByRole(role);
  const { selectedPermissions, setSelectedPermissions, permissionData, cols } = useFormData(allPermissions, permissions, accessibleFeatures, disabled);
  const [open, setOpen] = useState(false);
  const [roleName, setRoleName] = useState(formatName(role));
  const [errorMessage, setErrorMessage] = useState('');
  const { refreshRoles, refreshUsersToRole } = onSaveSuccess;
  const advertiser = account.pixel_source_name;

  const onClose = () => {
    setOpen(false);
    setSelectedPermissions(permissions);
    refreshPermissions();
  };

  const onRoleNameChange = (e, { value }) => {
    setRoleName(value);
    if (!isValidRoleName(value)) {
      setErrorMessage('Role cannot be empty or contain any special characters.');
    } else {
      setErrorMessage('');
    }
  };

  const createNewRole = () => {
    const formattedRoleName = roleName.replace(/\s/g, '_').toLowerCase();
    Promise.all([
      postRolesInRBAC([formattedRoleName]),
      postPermissionsForRoleInRBAC(formattedRoleName, selectedPermissions),
    ])
      .then(() => {
        toast.success(`Successfully saved permissions for role ${roleName}.`);
      })
      .catch(error => {
        toast.error(`Failed to create role and permissions for role ${roleName} with error: ${error}.`);
      })
      .finally(() => {
        refreshRoles();
        refreshUsersToRole();
        onClose();
        track('settings.rbac.custom_role_created', {
          advertiser,
          role_name: roleName,
        });
      });
  };

  const updateRole = () => {
    const formattedRoleName = roleName.replace(/\s/g, '_').toLowerCase();
    const promises = [
      putRoleInRBAC(role, formattedRoleName),
    ];
    if (!arePermissionsEqual(selectedPermissions, permissions)) {
      promises.push(putPermissionsForRoleInRBAC(formattedRoleName, selectedPermissions));
    }
    Promise.all(promises)
      .then(() => {
        toast.success(`Successfully updated permissions for role ${roleName}.`);
      })
      .catch(error => {
        toast.error(`Failed to update role and permissions for role ${roleName} with error: ${error}.`);
      })
      .finally(() => {
        refreshRoles();
        refreshUsersToRole();
        onClose();
        track('settings.rbac.role_permission_change', {
          advertiser,
          role_name: roleName,
          updated_permissions: selectedPermissions,
        });
      });
  };

  const isSystemRole = RBAC_SYSTEM_ROLES.includes(role);

  return (
    <Modal
      closeIcon
      onClose={onClose}
      onOpen={() => setOpen(true)}
      open={open}
      trigger={trigger}
    >
      <Modal.Header>
        <Input
          placeholder="Enter Custom Role Name..."
          value={roleName}
          disabled={isSystemRole}
          onChange={onRoleNameChange}
        />
        {' '}
        Permissions
        {errorMessage && (
          <Message negative>
            <p>{errorMessage}</p>
          </Message>
        )}
      </Modal.Header>
      <Modal.Content scrolling>
        <Modal.Description>
          <IndexGridTree
            data={permissionData}
            cols={cols}
          />
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button
          onClick={onClose}
          content="Cancel"
        />
        <Button
          primary
          content="Save"
          onClick={role ? updateRole : createNewRole}
          disabled={disabled || !isValidRoleName(roleName)}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default PermissionForm;
